import { useState } from 'react';

const TABS = [
  {
    id: 'analyzer',
    label: 'Title Analyzer',
    hero: {
      title: 'YouTube Title Analyzer',
      desc: "Paste any YouTube URL and instantly see what makes the title work. Extract keywords from the transcript, score readability, detect power words, and get AI-generated title alternatives that boost your click-through rate.",
      ctaText: 'Analyze a Video Free',
      ctaLink: '/#/app',
      hint: 'No account required for text analysis',
    },
    sectionTitle: 'What you get from every analysis',
    sectionSub: "Drop a YouTube URL or paste raw text. The analyzer breaks down the content and gives you data-backed insights to write better titles.",
    features: [
      {
        title: 'Keyword Extraction',
        desc: "Automatically pull the most-used words and phrases from any video transcript. See exactly what topics the video covers and which keywords drive the content.",
        icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#a78bfa"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95a15.65 15.65 0 0 0-1.38-3.56A8.03 8.03 0 0 1 18.92 8zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96z"/></svg>,
      },
      {
        title: 'Readability Scoring',
        desc: "Get a Flesch-Kincaid readability grade for your title. The best YouTube titles are written at a 6th-8th grade level \u2014 easy to scan in a feed.",
        icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#a78bfa"><path d="M12 6.5a9.77 9.77 0 0 1 8.82 5.5c-1.65 3.37-5.02 5.5-8.82 5.5S4.83 15.37 3.18 12A9.77 9.77 0 0 1 12 6.5m0-2C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zm0 5a2.5 2.5 0 0 1 0 5 2.5 2.5 0 0 1 0-5m0-2c-2.48 0-4.5 2.02-4.5 4.5s2.02 4.5 4.5 4.5 4.5-2.02 4.5-4.5-2.02-4.5-4.5-4.5z"/></svg>,
      },
      {
        title: 'Power Word Detection',
        desc: 'Identify emotional trigger words that drive clicks. Words like "secret," "proven," "insane," and "exposed" increase CTR by activating curiosity and urgency.',
        icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#a78bfa"><path d="M7 2v11h3v9l7-12h-4l4-8z"/></svg>,
      },
      {
        title: 'Topic Timeline',
        desc: "Visualize when keywords appear throughout a video. See which topics dominate the intro, middle, and end to understand content structure.",
        icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#a78bfa"><path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/></svg>,
      },
      {
        title: 'AI Title Generator',
        desc: "Get AI-generated title alternatives in three styles: curiosity-driven, value-focused, and emotional. Based on the actual content of your video.",
        icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#6ee7b7"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>,
        pro: true,
      },
      {
        title: 'Sentiment & Hook Analysis',
        desc: "Understand the emotional tone of your content. Get the best quotes for thumbnails and opening hook suggestions that keep viewers watching.",
        icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#6ee7b7"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/></svg>,
        pro: true,
      },
    ],
    steps: [
      { title: 'Paste a YouTube URL or raw text', desc: 'Enter any YouTube video URL to auto-fetch the transcript, or paste your own script or text directly. Works with any video that has captions.' },
      { title: 'Get instant keyword analysis', desc: 'See the top single keywords and multi-word phrases, readability score, power words, question hooks, and a timeline of when topics appear in the video.' },
      { title: 'Generate optimized titles with AI', desc: 'AI analyzes your transcript and generates click-worthy title alternatives. Also get optimized descriptions, SEO tags, and opening hook suggestions.' },
    ],
    also: { label: 'CTR Simulator', desc: 'Test titles against 100K synthetic viewers', switchTo: 1 },
  },
  {
    id: 'simulator',
    label: 'CTR Simulator',
    hero: {
      title: 'YouTube CTR Simulator',
      desc: "Test your video titles against 100,000 synthetic viewers before you publish. See predicted click-through rates broken down by viewer type, compare up to 3 titles side by side, and get AI-powered analysis of what works and what doesn't.",
      ctaText: 'Run a Simulation',
      ctaLink: '/#/simulator',
      hint: '1 credit per run. Sign up for 3 free credits.',
    },
    sectionTitle: 'What the simulator does',
    sectionSub: "Think of it as a YouTube sandbox. Your title gets shown to 100,000 simulated viewers across 10 independent rounds, and the results are averaged for reliability.",
    features: [
      {
        title: 'Sandbox Simulation',
        desc: "Each title runs through 10 independent rounds of 100K viewers in a realistic sandbox. Results are averaged across runs to give you reliable, consistent CTR predictions.",
        icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#a78bfa"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z"/></svg>,
      },
      {
        title: '9 Viewer Archetypes',
        desc: "Not all viewers click the same way. Your title is tested against Casual Scrollers, Impulse Clickers, Knowledge Seekers, Skeptics, Deep Divers, and more.",
        icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#a78bfa"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>,
      },
      {
        title: 'Compare Up to 3 Titles',
        desc: "A/B/C test your title ideas before publishing. See which one wins across all viewer types, with win counts and confidence intervals per round.",
        icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#a78bfa"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>,
      },
      {
        title: 'AI Signal Scoring',
        desc: "AI scores your title across 18 signal dimensions including curiosity, emotional pull, urgency, specificity, and narrative strength.",
        icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#a78bfa"><path d="M7 2v11h3v9l7-12h-4l4-8z"/></svg>,
      },
      {
        title: 'Strengths & Weaknesses',
        desc: "See exactly which signals are strong and which are weak. Understand why certain viewer types click and others scroll past your title.",
        icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#6ee7b7"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>,
        pro: true,
      },
      {
        title: 'AI Rewrite Suggestions',
        desc: "After the simulation, AI analyzes the results and suggests a rewritten title that addresses weaknesses while keeping your strengths.",
        icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#6ee7b7"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>,
        pro: true,
      },
    ],
    archetypes: [
      { name: 'Casual Scroller', desc: 'Moderate attention, scrolls through feed at average speed' },
      { name: 'Distracted Scroller', desc: 'Barely reading titles, extremely low engagement' },
      { name: 'Passive Viewer', desc: 'Low energy browser, occasionally clicks' },
      { name: 'Impulse Clicker', desc: 'Clicks on flashy, urgent, dramatic content' },
      { name: 'Trend Follower', desc: 'Drawn to trending formats and social proof' },
      { name: 'Knowledge Seeker', desc: 'Wants depth, drawn to "why" and "how"' },
      { name: 'Skeptic', desc: 'Distrusts clickbait, values substance' },
      { name: 'Deep Diver', desc: 'Loves long-form, expert, niche content' },
      { name: 'Binge Watcher', desc: 'High engagement, loves stories and series' },
    ],
    steps: [
      { title: 'Enter 1-3 title ideas', desc: "Type in the titles you're considering for your next video. Compare them head-to-head to find the winner before you publish." },
      { title: 'AI scores your titles across 18 signals', desc: 'AI evaluates curiosity, emotional pull, power words, urgency, narrative strength, and more. These scores feed into the simulation engine.' },
      { title: '100K viewers decide', desc: "The simulation engine generates 100,000 synthetic viewers across 9 archetypes. Each viewer makes an independent click/skip decision based on the title's signals and their own personality." },
      { title: 'Get your results', desc: 'See predicted CTR, per-archetype breakdown, strengths and weaknesses, and AI-suggested rewrites. Results are averaged across 10 rounds for reliability.' },
    ],
    also: { label: 'Title Analyzer', desc: 'Extract keywords and score any YouTube title', switchTo: 0 },
  },
];

const FeaturesPage = ({ onGetStarted, onSignIn }) => {
  const [activeTab, setActiveTab] = useState(0);
  const tab = TABS[activeTab];
  const handleCta = onSignIn || onGetStarted;

  return (
    <div style={s.page}>
      {/* Tab toggle */}
      <div style={s.tabBar}>
        <div style={s.tabRow}>
          {TABS.map((t, i) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(i)}
              style={i === activeTab ? s.tabActive : s.tab}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Hero */}
      <section style={s.hero}>
        <h1 style={s.heroTitle}>{tab.hero.title}</h1>
        <p style={s.heroDesc}>{tab.hero.desc}</p>
        <button onClick={handleCta} style={s.ctaBtn}>{tab.hero.ctaText}</button>
        <p style={s.ctaHint}>{tab.hero.hint}</p>
      </section>

      <div style={s.container}>
        {/* Features */}
        <section style={s.section}>
          <h2 style={s.sectionTitle}>{tab.sectionTitle}</h2>
          <p style={s.sectionSub}>{tab.sectionSub}</p>
          <div style={s.featuresGrid} className="features-page-grid">
            {tab.features.map((f, i) => (
              <div key={i} style={f.pro ? s.featureCardPro : s.featureCard} className="features-page-card">
                <div style={f.pro ? s.featureIconPro : s.featureIcon}>{f.icon}</div>
                {f.pro && <span style={s.proBadge}>PRO</span>}
                <h3 style={s.featureTitle}>{f.title}</h3>
                <p style={s.featureDesc}>{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Archetypes (simulator only) */}
        {tab.archetypes && (
          <section style={s.section}>
            <h2 style={s.sectionTitle}>The viewer archetypes</h2>
            <p style={s.sectionSub}>YouTube's audience isn't one group. The simulator models 9 distinct viewer types with different attention levels, click thresholds, and content preferences.</p>
            <div style={s.archetypeGrid} className="features-page-archetype-grid">
              {tab.archetypes.map((a, i) => (
                <div key={i} style={s.archetypeCard}>
                  <p style={s.archetypeName}>{a.name}</p>
                  <p style={s.archetypeDesc}>{a.desc}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* How it works */}
        <section style={s.section}>
          <h2 style={s.sectionTitle}>How it works</h2>
          <div>
            {tab.steps.map((step, i) => (
              <div key={i} style={s.step}>
                <div style={s.stepNum}>{i + 1}</div>
                <div>
                  <h3 style={s.stepTitle}>{step.title}</h3>
                  <p style={s.stepDesc}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Also from Titler */}
        <div style={s.alsoSection}>
          <p style={s.alsoTitle}>Also from Titler</p>
          <button
            onClick={() => { setActiveTab(tab.also.switchTo); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            style={s.alsoLink}
          >
            {tab.also.label} &mdash; {tab.also.desc}
            <svg viewBox="0 0 24 24" width="14" height="14" fill="#64748b"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/></svg>
          </button>
        </div>
      </div>

      {/* Bottom CTA */}
      <div style={s.bottomCta}>
        <h2 style={s.bottomCtaTitle}>
          {activeTab === 0 ? 'Ready to optimize your YouTube titles?' : 'Test your titles before the algorithm does'}
        </h2>
        <p style={s.bottomCtaDesc}>
          {activeTab === 0 ? 'Paste a URL or text and see your analysis in seconds.' : "Find out which title gets more clicks \u2014 before you publish."}
        </p>
        <button onClick={handleCta} style={s.ctaBtn}>{tab.hero.ctaText}</button>
      </div>

      <footer style={s.footer}>
        <span>Titler &mdash; Content Analyzer & Title Optimizer for Creators</span>
      </footer>

      <style>{`
        @media (max-width: 600px) {
          .features-page-grid { grid-template-columns: 1fr !important; }
          .features-page-archetype-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </div>
  );
};

const s = {
  page: {
    minHeight: '100vh',
    background: '#000',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: '#e2e8f0',
  },
  tabBar: {
    display: 'flex', justifyContent: 'center', padding: '24px 24px 0',
  },
  tabRow: {
    display: 'flex', gap: '4px',
    background: 'rgba(255,255,255,0.04)', borderRadius: '12px', padding: '4px',
  },
  tab: {
    padding: '10px 24px', borderRadius: '10px', border: 'none',
    background: 'transparent', color: '#64748b', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
  },
  tabActive: {
    padding: '10px 24px', borderRadius: '10px', border: 'none',
    background: 'rgba(167,139,250,0.15)', color: '#a78bfa', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
  },
  hero: {
    textAlign: 'center', padding: '48px 24px 56px', maxWidth: '900px', margin: '0 auto',
  },
  heroTitle: {
    fontSize: '42px', fontWeight: '800', letterSpacing: '-2px', marginBottom: '16px', marginTop: 0,
    background: 'linear-gradient(135deg, #e2e8f0, #a78bfa)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: '1.1',
  },
  heroDesc: {
    fontSize: '17px', color: '#94a3b8', maxWidth: '620px', margin: '0 auto 28px', lineHeight: '1.7',
  },
  ctaBtn: {
    display: 'inline-block', padding: '14px 36px', borderRadius: '14px', border: 'none',
    background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
    color: '#fff', fontSize: '16px', fontWeight: '700', cursor: 'pointer',
    textDecoration: 'none',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  ctaHint: {
    fontSize: '13px', color: '#475569', marginTop: '12px',
  },
  container: {
    maxWidth: '900px', margin: '0 auto', padding: '0 24px 48px',
  },
  section: {
    marginBottom: '56px',
  },
  sectionTitle: {
    fontSize: '28px', fontWeight: '800', letterSpacing: '-1px', marginBottom: '12px', marginTop: 0,
    color: '#e2e8f0',
  },
  sectionSub: {
    fontSize: '15px', color: '#64748b', marginBottom: '28px', lineHeight: '1.7',
  },
  featuresGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px',
  },
  featureCard: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '14px', padding: '24px', position: 'relative',
    transition: 'transform 0.2s, border-color 0.2s',
  },
  featureCardPro: {
    background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.15)',
    borderRadius: '14px', padding: '24px', position: 'relative',
    transition: 'transform 0.2s, border-color 0.2s',
  },
  featureIcon: {
    width: '44px', height: '44px', borderRadius: '12px',
    background: 'rgba(124,58,237,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: '14px',
  },
  featureIconPro: {
    width: '44px', height: '44px', borderRadius: '12px',
    background: 'rgba(16,185,129,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: '14px',
  },
  proBadge: {
    position: 'absolute', top: '16px', right: '16px',
    padding: '2px 8px', borderRadius: '6px',
    background: 'rgba(16,185,129,0.15)', color: '#6ee7b7',
    fontSize: '10px', fontWeight: '700', letterSpacing: '0.5px',
  },
  featureTitle: {
    fontSize: '16px', fontWeight: '700', color: '#e2e8f0', marginBottom: '6px', marginTop: 0,
  },
  featureDesc: {
    fontSize: '13px', color: '#94a3b8', lineHeight: '1.6', margin: 0,
  },
  archetypeGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px',
  },
  archetypeCard: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '10px', padding: '16px', textAlign: 'center',
  },
  archetypeName: {
    fontSize: '13px', fontWeight: '700', color: '#e2e8f0', marginBottom: '4px', marginTop: 0,
  },
  archetypeDesc: {
    fontSize: '11px', color: '#64748b', lineHeight: '1.5', margin: 0,
  },
  step: {
    display: 'flex', gap: '20px', alignItems: 'flex-start',
    padding: '20px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
  },
  stepNum: {
    width: '36px', height: '36px', borderRadius: '10px',
    background: 'rgba(124,58,237,0.12)', color: '#a78bfa',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '14px', fontWeight: '800', flexShrink: 0,
  },
  stepTitle: {
    fontSize: '15px', fontWeight: '700', color: '#e2e8f0', marginBottom: '4px', marginTop: 0,
  },
  stepDesc: {
    fontSize: '13px', color: '#94a3b8', lineHeight: '1.6', margin: 0,
  },
  alsoSection: {
    marginTop: '8px', padding: '28px', borderRadius: '14px',
    background: 'rgba(124,58,237,0.04)', border: '1px solid rgba(124,58,237,0.1)',
  },
  alsoTitle: {
    fontSize: '14px', fontWeight: '700', color: '#a78bfa', marginBottom: '8px', marginTop: 0,
  },
  alsoLink: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '8px 16px', borderRadius: '8px',
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
    fontSize: '13px', fontWeight: '600', color: '#e2e8f0',
    cursor: 'pointer', fontFamily: 'inherit',
    transition: 'border-color 0.2s',
  },
  bottomCta: {
    textAlign: 'center', padding: '48px 24px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
  },
  bottomCtaTitle: {
    fontSize: '24px', fontWeight: '800', color: '#e2e8f0', marginBottom: '12px', marginTop: 0,
    letterSpacing: '-0.5px',
  },
  bottomCtaDesc: {
    fontSize: '14px', color: '#64748b', marginBottom: '24px', marginTop: 0,
  },
  footer: {
    textAlign: 'center', padding: '24px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    fontSize: '13px', color: '#333',
  },
};

export default FeaturesPage;
